
import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useData } from '../../context/DataContext';
import { BlogPost } from '../../types';
import {
   Plus, Edit, Trash2, X, Check, Search, Image as ImageIcon, Video, Tag, Eye,
   Upload, Link as LinkIcon, List, Type, Quote, Code, AlignLeft, AlignCenter, AlignRight,
   AlignJustify, Bold, Italic, Underline, MoreHorizontal
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
// @ts-ignore
import BlotFormatter from 'quill-blot-formatter';

// Register BlotFormatter
const Quill = ReactQuill.Quill;
Quill.register('modules/blotFormatter', BlotFormatter);

type Tab = 'content' | 'media' | 'seo' | 'settings';

const BlogManager: React.FC = () => {
   const { posts, addPost, updatePost, deletePost, settings } = useData();
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [editingPost, setEditingPost] = useState<Partial<BlogPost>>({});
   const [activeTab, setActiveTab] = useState<Tab>('content');
   const [uploading, setUploading] = useState(false);
   const [tagInput, setTagInput] = useState('');
   const [showPreview, setShowPreview] = useState(false);

   // Link Modal State
   const [showLinkModal, setShowLinkModal] = useState(false);
   const [linkData, setLinkData] = useState({ text: '', url: '', isInternal: false, internalId: '' });

   // Editor Ref
   const quillRef = useRef<any>(null);

   const openModal = (post?: BlogPost) => {
      setActiveTab('content');
      setShowPreview(false);
      if (post) {
         setEditingPost({ ...post });
      } else {
         setEditingPost({
            id: Math.random().toString(36).substr(2, 9),
            title: '',
            slug: '',
            excerpt: '',
            content: '',
            author: 'Admin',
            date: new Date().toISOString().split('T')[0],
            image: '',
            category: 'General',
            status: 'Draft',
            tags: [],
            metaTitle: '',
            metaDescription: ''
         });
      }
      setIsModalOpen(true);
   };

   const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (editingPost.id && editingPost.title) {
         const finalPost = {
            ...editingPost,
            slug: editingPost.slug || editingPost.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, ''),
            metaTitle: editingPost.metaTitle || editingPost.title,
            metaDescription: editingPost.metaDescription || editingPost.excerpt,
            image: editingPost.image || 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1000'
         } as BlogPost;

         const exists = posts.some(p => p.id === finalPost.id);
         if (exists) {
            updatePost(finalPost);
         } else {
            addPost(finalPost);
         }
         setIsModalOpen(false);
      }
   };

   const handleChange = (field: keyof BlogPost, value: any) => {
      setEditingPost(prev => ({ ...prev, [field]: value }));
   };

   const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const title = e.target.value;
      const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
      setEditingPost(prev => ({
         ...prev,
         title,
         slug: (!prev.slug || prev.slug === prev.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')) ? slug : prev.slug
      }));
   };

   const handleAddTag = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && tagInput.trim()) {
         e.preventDefault();
         const currentTags = editingPost.tags || [];
         if (!currentTags.includes(tagInput.trim())) {
            handleChange('tags', [...currentTags, tagInput.trim()]);
         }
         setTagInput('');
      }
   };

   const removeTag = (tag: string) => {
      const currentTags = editingPost.tags || [];
      handleChange('tags', currentTags.filter(t => t !== tag));
   };

   // --- CMS Editor Functions ---

   // Custom Image Handler for Quill
   const imageHandler = useCallback(() => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();

      input.onchange = async () => {
         const file = input.files ? input.files[0] : null;
         if (file) {
            try {
               setUploading(true);
               // Upload to Firebase Storage
               const storageRef = ref(storage, `blog-images/${Date.now()}_${file.name}`);
               const snapshot = await uploadBytes(storageRef, file);
               const url = await getDownloadURL(snapshot.ref);
               setUploading(false);

               // Insert image into editor
               const quill = quillRef.current?.getEditor();
               const range = quill?.getSelection();
               if (quill && range) {
                  quill.insertEmbed(range.index, 'image', url);
               }
            } catch (error: any) {
               console.error("Error uploaded image: ", error);
               setUploading(false);
               let errorMessage = "Image upload failed. Please try again.";
               if (error.code === 'storage/unauthorized') {
                  errorMessage = "Permission denied. Please check your Firebase Storage rules.";
               } else if (error.code === 'storage/canceled') {
                  errorMessage = "Upload canceled.";
               } else if (error.code === 'storage/retry-limit-exceeded') {
                  errorMessage = "Upload failed. Retry limit exceeded.";
               } else if (error.code === 'storage/unknown') {
                  errorMessage = "An unknown error has occurred.";
               }
               alert(errorMessage);
            }
         }
         // Reset input
         input.value = '';
      };
   }, []);

   // React Quill Modules
   // Register Fonts
   const Quill = ReactQuill.Quill;
   var Font = Quill.import('attributors/style/font');
   const fonts = [
      'inter', 'teko', 'roboto', 'open-sans', 'lato', 'montserrat', 'oswald', 'source-sans-pro',
      'slabo-27px', 'raleway', 'pt-sans', 'merriweather', 'noto-sans', 'nunito', 'concert-one',
      'prompt', 'work-sans', 'poppins', 'quicksand', 'rubik', 'oxygen', 'fira-sans', 'mukta',
      'barlow', 'ibm-plex-sans', 'ubuntu', 'crimson-text', 'playfair-display', 'lora', 'bitter',
      'arvo', 'anton'
   ];
   Font.whitelist = fonts;
   Quill.register(Font, true);

   const modules = useMemo(() => ({
      toolbar: {
         container: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'font': fonts }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['clean']
         ],
         handlers: {
            image: imageHandler
         }
      },
      clipboard: {
         matchVisual: false
      },
      blotFormatter: {}
   }), [imageHandler]);

   // General File Upload (Featured Image/Video)
   const handleFeaturedUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
      const file = e.target.files?.[0];
      if (file) {
         setUploading(true);
         try {
            const path = type === 'video' ? 'blog-videos' : 'blog-images';
            const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const url = await getDownloadURL(snapshot.ref);

            if (type === 'image') handleChange('image', url);
            else handleChange('featuredVideo', url);
         } catch (error: any) {
            console.error("Upload failed", error);
            let errorMessage = "Upload failed. Please try again.";
            if (error.code === 'storage/unauthorized') {
               errorMessage = "Permission denied. Please check your Firebase Storage rules.";
            } else if (error.code === 'storage/canceled') {
               errorMessage = "Upload canceled.";
            } else if (error.code === 'storage/unknown') {
               errorMessage = "An unknown error has occurred.";
            }
            alert(errorMessage);
         }
         setUploading(false);
         // Reset input value so the same file can be selected again
         e.target.value = '';
      }
   };

   // Stats
   const wordCount = editingPost.content ? editingPost.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0;
   const readTime = Math.ceil(wordCount / 200);

   return (
      <div>
         <div className="flex justify-between items-center mb-8">
            <div>
               <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
               <p className="text-gray-500">Professional CMS for news, reviews, and updates.</p>
            </div>
            <button
               onClick={() => openModal()}
               className="bg-automax-red hover:bg-red-700 text-white px-6 py-3 rounded-sm font-bold flex items-center space-x-2 transition-colors shadow-lg"
            >
               <Plus size={20} />
               <span>Write New Article</span>
            </button>
         </div>

         {/* List Table */}
         <div className="bg-white rounded-sm shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
               <table className="w-full text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                     <tr>
                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Article</th>
                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Stats</th>
                        <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                     {posts.map(post => (
                        <tr key={post.id} className="hover:bg-gray-50">
                           <td className="p-4">
                              <span className={`px-2 py-1 rounded-sm text-xs font-bold uppercase ${post.status === 'Published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                 {post.status || 'Draft'}
                              </span>
                           </td>
                           <td className="p-4">
                              <div className="flex items-center">
                                 <img src={post.image || 'https://via.placeholder.com/150'} alt={post.title} className="w-12 h-12 rounded-sm object-cover mr-4" />
                                 <div>
                                    <div className="font-bold text-gray-900 line-clamp-1">{post.title}</div>
                                    <div className="text-xs text-gray-400 mt-1 flex items-center gap-2">
                                       <span>{post.author}</span> • <span>{new Date(post.date).toLocaleDateString()}</span>
                                    </div>
                                 </div>
                              </div>
                           </td>
                           <td className="p-4 text-sm text-gray-600">
                              <span className="bg-gray-100 px-2 py-1 rounded-sm text-xs font-bold">{post.category}</span>
                           </td>
                           <td className="p-4">
                              <div className="text-xs text-gray-500">
                                 <span className="block">{post.content.length} chars</span>
                                 <span className="block">{post.metaDescription ? 'SEO OK' : 'No SEO'}</span>
                              </div>
                           </td>
                           <td className="p-4 text-right space-x-2">
                              <button onClick={() => openModal(post)} className="text-gray-400 hover:text-blue-600 transition-colors">
                                 <Edit size={18} />
                              </button>
                              <button onClick={() => { if (window.confirm('Are you sure?')) deletePost(post.id) }} className="text-gray-400 hover:text-red-600 transition-colors">
                                 <Trash2 size={18} />
                              </button>
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
         </div>

         {/* Professional CMS Modal */}
         {isModalOpen && (
            <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
               <div className="bg-white rounded-sm shadow-2xl w-full max-w-6xl h-[95vh] flex flex-col overflow-hidden relative">

                  {/* Modal Header */}
                  <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50">
                     <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-gray-900">{editingPost.id && posts.some(p => p.id === editingPost.id) ? 'Edit Post' : 'New Post'}</h2>
                        <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded-sm border ${editingPost.status === 'Published' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                           {editingPost.status || 'Draft'}
                        </span>
                     </div>
                     <div className="flex items-center gap-4">
                        <button onClick={() => setShowPreview(!showPreview)} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-bold transition-colors ${showPreview ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                           <Eye size={16} /> {showPreview ? 'Edit Mode' : 'Live Preview'}
                        </button>
                        <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-900"><X size={24} /></button>
                     </div>
                  </div>

                  <form onSubmit={handleSubmit} className="flex-1 flex overflow-hidden">

                     {/* Left Sidebar Navigation */}
                     <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col justify-between p-4 flex-shrink-0">
                        <nav className="space-y-1">
                           {[
                              { id: 'content', label: 'Content Editor', icon: <Edit size={18} /> },
                              { id: 'media', label: 'Featured Media', icon: <ImageIcon size={18} /> },
                              { id: 'seo', label: 'SEO & Search', icon: <Search size={18} /> },
                              { id: 'settings', label: 'Publish Settings', icon: <Tag size={18} /> }
                           ].map(item => (
                              <button
                                 key={item.id}
                                 type="button"
                                 onClick={() => { setActiveTab(item.id as Tab); setShowPreview(false); }}
                                 className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-bold rounded-sm transition-colors ${activeTab === item.id ? 'bg-white text-automax-red shadow-sm border border-gray-200' : 'text-gray-600 hover:bg-gray-200'}`}
                              >
                                 {item.icon} {item.label}
                              </button>
                           ))}
                        </nav>

                        <div className="space-y-3 pt-6 border-t border-gray-200">
                           <div className="bg-blue-50 p-3 rounded-sm mb-2 text-xs text-blue-800">
                              <p className="font-bold mb-1">Article Stats</p>
                              <p>{wordCount} words</p>
                              <p>~{readTime} min read</p>
                           </div>
                           <button
                              type="button"
                              onClick={() => handleChange('status', 'Draft')}
                              className="w-full py-2 bg-gray-200 text-gray-700 font-bold text-sm rounded-sm hover:bg-gray-300 transition-colors"
                           >
                              Save as Draft
                           </button>
                           <button
                              type="submit"
                              onClick={() => handleChange('status', 'Published')}
                              className="w-full py-2 bg-automax-red text-white font-bold text-sm rounded-sm hover:bg-red-700 transition-colors flex justify-center items-center gap-2"
                           >
                              <Check size={16} /> Publish
                           </button>
                        </div>
                     </div>

                     {/* Main Content Area */}
                     <div className="flex-1 overflow-y-auto bg-white relative">

                        {/* TAB: CONTENT */}
                        {activeTab === 'content' && (
                           <div className="h-full flex flex-col">

                              {/* Title Section */}
                              <div className="p-8 pb-4 border-b border-gray-100">
                                 <input
                                    required
                                    type="text"
                                    value={editingPost.title}
                                    onChange={handleTitleChange}
                                    className="w-full text-4xl font-display font-bold focus:outline-none placeholder-gray-300"
                                    placeholder="Enter Article Title Here..."
                                 />
                                 <input
                                    type="text"
                                    value={editingPost.excerpt}
                                    onChange={e => handleChange('excerpt', e.target.value)}
                                    className="w-full mt-4 p-2 bg-gray-50 border-none text-gray-600 italic focus:ring-0 focus:bg-white transition-colors"
                                    placeholder="Type a short excerpt or summary..."
                                 />
                              </div>

                              {showPreview ? (
                                 <div className="flex-1 p-8 overflow-y-auto bg-gray-50">
                                    <div className="prose prose-lg max-w-none mx-auto bg-white p-12 shadow-sm rounded-sm border border-gray-100">
                                       <h1 className="font-display text-4xl font-bold mb-4 uppercase">{editingPost.title}</h1>
                                       {editingPost.image && <img src={editingPost.image} alt="Featured" className="w-full h-96 object-cover rounded-sm mb-8" />}
                                       <div dangerouslySetInnerHTML={{ __html: editingPost.content || '' }} />
                                    </div>
                                 </div>
                              ) : (
                                 <div className="h-full bg-white flex flex-col">
                                    {uploading && (
                                       <div className="bg-blue-100 text-blue-800 text-xs font-bold px-4 py-2 text-center">
                                          Uploading media... Please wait.
                                       </div>
                                    )}
                                    {/* @ts-ignore */}
                                    <ReactQuill
                                       ref={quillRef}
                                       theme="snow"
                                       value={editingPost.content || ''}
                                       onChange={(content) => handleChange('content', content)}
                                       modules={modules}
                                       className="h-full flex flex-col"
                                    />
                                    {/* Link Modal Removed */}
                                 </div>
                              )}
                           </div>
                        )}

                        {/* TAB: MEDIA */}
                        {activeTab === 'media' && (
                           <div className="p-8 space-y-8 max-w-3xl mx-auto">
                              <div className="border border-gray-200 rounded-sm p-6 bg-gray-50">
                                 <div className="flex justify-between items-center mb-4">
                                    <label className="block text-lg font-bold text-gray-900"><ImageIcon size={20} className="inline mr-2" /> Featured Image</label>
                                    {uploading && <span className="text-xs text-automax-red font-bold animate-pulse">Uploading...</span>}
                                 </div>
                                 <div className="flex gap-6 items-start">
                                    <div className="w-1/2 aspect-video bg-gray-200 rounded-sm overflow-hidden relative border border-gray-300">
                                       {editingPost.image ? (
                                          <img src={editingPost.image} alt="Preview" className="w-full h-full object-cover" />
                                       ) : (
                                          <div className="flex items-center justify-center h-full text-gray-400">No Image</div>
                                       )}
                                    </div>
                                    <div className="flex-1 space-y-4">
                                       <div className="relative">
                                          <input
                                             type="file"
                                             accept="image/*"
                                             onChange={(e) => handleFeaturedUpload(e, 'image')}
                                             disabled={uploading}
                                             className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                          />
                                          <button type="button" className="w-full bg-white border border-gray-300 text-gray-700 py-3 font-bold rounded-sm hover:bg-gray-50 flex items-center justify-center gap-2">
                                             <Upload size={18} /> Upload Featured Image
                                          </button>
                                       </div>
                                       <div className="text-center text-sm text-gray-500">- OR -</div>
                                       <input
                                          type="text"
                                          placeholder="Paste Image URL"
                                          value={editingPost.image}
                                          onChange={e => handleChange('image', e.target.value)}
                                          className="w-full border p-3 rounded-sm text-sm"
                                       />
                                    </div>
                                 </div>
                              </div>

                              <div className="border border-gray-200 rounded-sm p-6 bg-gray-50">
                                 <div className="flex justify-between items-center mb-4">
                                    <label className="block text-lg font-bold text-gray-900"><Video size={20} className="inline mr-2" /> Featured Video (Optional)</label>
                                 </div>
                                 <div className="flex gap-6 items-start">
                                    <div className="w-1/2 aspect-video bg-black rounded-sm overflow-hidden relative">
                                       {editingPost.featuredVideo ? (
                                          <video src={editingPost.featuredVideo} controls className="w-full h-full" />
                                       ) : (
                                          <div className="flex items-center justify-center h-full text-gray-500">No Video</div>
                                       )}
                                    </div>
                                    <div className="flex-1 space-y-4">
                                       <div className="relative">
                                          <input
                                             type="file"
                                             accept="video/*"
                                             onChange={(e) => handleFeaturedUpload(e, 'video')}
                                             disabled={uploading}
                                             className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                          />
                                          <button type="button" className="w-full bg-white border border-gray-300 text-gray-700 py-3 font-bold rounded-sm hover:bg-gray-50 flex items-center justify-center gap-2">
                                             <Upload size={18} /> Upload Video
                                          </button>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           </div>
                        )}

                        {/* TAB: SEO */}
                        {activeTab === 'seo' && (
                           <div className="p-8 space-y-8 max-w-3xl mx-auto">
                              <div className="bg-white p-6 border border-gray-200 rounded-sm shadow-sm">
                                 <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Eye size={18} /> Google Search Preview</h3>
                                 <div className="font-sans">
                                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                                       <span className="w-6 h-6 bg-gray-200 rounded-full"></span>
                                       <span>automax.co.tz</span>
                                       <span>› blog › {editingPost.slug || 'your-post-url'}</span>
                                    </div>
                                    <div className="text-[#1a0dab] text-xl font-medium hover:underline cursor-pointer mb-1 truncate">
                                       {editingPost.metaTitle || editingPost.title || 'Your Post Title'}
                                    </div>
                                    <div className="text-sm text-gray-600 line-clamp-2">
                                       {editingPost.metaDescription || editingPost.excerpt || 'Please enter a meta description to preview how your page will look in search engine results.'}
                                    </div>
                                 </div>
                              </div>

                              <div className="grid grid-cols-1 gap-6">
                                 <div>
                                    <label className="block text-sm font-bold text-gray-900 mb-2">URL Slug</label>
                                    <div className="flex">
                                       <span className="bg-gray-100 border border-r-0 border-gray-300 p-2 text-gray-500 text-sm flex items-center rounded-l-sm">automax.co.tz/blog/</span>
                                       <input type="text" value={editingPost.slug} onChange={e => handleChange('slug', e.target.value)} className="flex-1 border border-gray-300 p-2 rounded-r-sm text-sm" />
                                    </div>
                                 </div>

                                 <div>
                                    <label className="block text-sm font-bold text-gray-900 mb-2">Meta Title</label>
                                    <input type="text" value={editingPost.metaTitle} onChange={e => handleChange('metaTitle', e.target.value)} className="w-full border p-2 rounded-sm" placeholder="Title for search engines" />
                                 </div>

                                 <div>
                                    <label className="block text-sm font-bold text-gray-900 mb-2">Meta Description</label>
                                    <textarea rows={3} value={editingPost.metaDescription} onChange={e => handleChange('metaDescription', e.target.value)} className="w-full border p-2 rounded-sm" placeholder="Summary for search engines" />
                                 </div>
                              </div>
                           </div>
                        )}

                        {/* TAB: SETTINGS */}
                        {activeTab === 'settings' && (
                           <div className="p-8 space-y-6 max-w-3xl mx-auto">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                 <div>
                                    <label className="block text-sm font-bold text-gray-900 mb-2">Author</label>
                                    <input required type="text" value={editingPost.author} onChange={e => handleChange('author', e.target.value)} className="w-full border p-2 rounded-sm" />
                                 </div>
                                 <div>
                                    <label className="block text-sm font-bold text-gray-900 mb-2">Publish Date</label>
                                    <input required type="date" value={editingPost.date} onChange={e => handleChange('date', e.target.value)} className="w-full border p-2 rounded-sm" />
                                 </div>
                              </div>

                              <div>
                                 <label className="block text-sm font-bold text-gray-900 mb-2">Category</label>
                                 <select value={editingPost.category} onChange={e => handleChange('category', e.target.value)} className="w-full border p-2 rounded-sm bg-white">
                                    <option value="General">General</option>
                                    <option value="Maintenance">Maintenance</option>
                                    <option value="News">Company News</option>
                                    <option value="Reviews">Reviews</option>
                                    <option value="Industry">Industry Insights</option>
                                 </select>
                              </div>

                              <div>
                                 <label className="block text-sm font-bold text-gray-900 mb-2">Tags</label>
                                 <div className="border p-2 rounded-sm bg-white min-h-[48px] flex flex-wrap gap-2 mb-2">
                                    {editingPost.tags?.map(tag => (
                                       <span key={tag} className="bg-gray-200 px-2 py-1 rounded text-sm flex items-center gap-1">
                                          {tag} <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-600"><X size={12} /></button>
                                       </span>
                                    ))}
                                    <input
                                       type="text"
                                       value={tagInput}
                                       onChange={e => setTagInput(e.target.value)}
                                       onKeyDown={handleAddTag}
                                       className="outline-none flex-1 min-w-[120px]"
                                       placeholder="Type tag and hit Enter..."
                                    />
                                 </div>
                                 <p className="text-xs text-gray-500">Press Enter to add tags.</p>
                              </div>
                           </div>
                        )}

                     </div>
                  </form>
                  {/* Link Modal removed as it referenced deleted logic */}
               </div>
            </div>
         )}
      </div>
   );
};

export default BlogManager;
